import { CHAT_LABELS, CHAT_STARTER_STYLES } from "@repo/embed";
import { z } from "zod";

/**
 * The words and layout of the hosted chat that are the retailer's to choose. An empty text
 * means the built-in one, so a retailer who never opens this section changes nothing.
 */
export const chatSchema = z
	.object({
		/** The question on the first screen. Empty: "What are you shopping for today?". */
		welcomeTitle: z.string().max(80).default(""),
		/** One line under it. Empty: nothing. */
		welcomeSubtitle: z.string().max(140).default(""),
		/** Starter prompts as tiles with catalog photos, or a plain list. */
		starterStyle: z.enum(CHAT_STARTER_STYLES).default("photos"),
		/** The message box's placeholder. Empty: "Ask <assistant name>…". */
		placeholder: z.string().max(60).default(""),
		/** The retailer's logo in the header and beside each reply. */
		showLogo: z.boolean().default(true),
		/** Tappable next questions under a reply ("Anything cheaper?"). */
		followUps: z.boolean().default(true),
		/** The bar at the top. Off for an app that has its own; the widget keeps it (its close button). */
		showHeader: z.boolean().default(true),
		/** The basket button, the basket bar and the basket itself. Off when the app shows its own cart. */
		showBasket: z.boolean().default(true),
		/** The basket button in the header, while the basket is shown. */
		showBasketButton: z.boolean().default(true),
		/** The bar above the message box with the item count and the total. */
		showBasketBar: z.boolean().default(true),
		/** The checkout button in the basket. */
		showCheckout: z.boolean().default(true),
		/** "Start a new chat" in the header menu. */
		showNewChat: z.boolean().default(true),
		/** What the assistant is doing ("Searching the store…") above its results. */
		showSteps: z.boolean().default(true),
		/** Tapping a product opens the chat's own product sheet. */
		productSheet: z.boolean().default(true),
		/** The assistant's name above each reply. */
		showAssistantName: z.boolean().default(true),
		/** The suggested first messages on the welcome screen. */
		showStarters: z.boolean().default(true),
		/** The welcome screen's title, line and suggestions. Off when the app draws its own. */
		showWelcome: z.boolean().default(true),
		/** A back arrow before the logo that closes the chat, for one opened as a page. */
		showBack: z.boolean().default(false),
		/** The + on product cards: shoppers add without asking the assistant. */
		addFromCards: z.boolean().default(true),
		/** "Powered by Hephon" under the message box. Off only where Hephon allows it (OrganizationSettings). */
		poweredBy: z.boolean().default(true),
		/** Replacement wording for buttons and titles; an absent one is the built-in. */
		labels: z
			.object(
				Object.fromEntries(
					CHAT_LABELS.map((label) => [
						label,
						z.string().max(40).optional(),
					]),
				) as Record<
					(typeof CHAT_LABELS)[number],
					z.ZodOptional<z.ZodString>
				>,
			)
			.prefault({}),
	})
	.prefault({});
